import apiClient from '../utils/apiClient'

const KINDS = new Set(['PERSONAL', 'TEAM'])
const ROLES = new Set(['OWNER', 'ADMIN', 'MEMBER'])

function positiveId(value, field) {
	const normalized = String(value ?? '')
	if (!/^[1-9]\d*$/.test(normalized)) throw new TypeError(`Invalid ${field}`)
	return normalized
}

function nullableText(value) {
	return typeof value === 'string' ? value : null
}

function contacts(value, includeVisibility) {
	if (!Array.isArray(value)) throw new TypeError('Invalid contacts')
	return value.map(contact => Object.freeze({
		type: String(contact?.type || ''),
		value: String(contact?.value || ''),
		...(includeVisibility ? { isPublic: Boolean(contact?.isPublic) } : {}),
	}))
}

function normalizeOrganization(value, includeInternalId) {
	if (!value || !KINDS.has(value.kind) || !/^[1-9]\d{11}$/.test(String(value.reldoId || ''))) {
		throw new TypeError('Invalid organization profile')
	}
	return Object.freeze({
		...(includeInternalId ? { id: positiveId(value.id, 'organization.id') } : {}),
		kind: value.kind,
		reldoId: String(value.reldoId),
		name: nullableText(value.name),
		systemLabelKey: nullableText(value.systemLabelKey),
		logoObjectKey: nullableText(value.logoObjectKey),
		publicContacts: contacts(value.publicContacts || [], false),
	})
}

function normalizeMembership(value) {
	if (!value || !ROLES.has(value.role) || value.status !== 'ACTIVE') throw new TypeError('Invalid membership profile')
	return Object.freeze({
		id: positiveId(value.id, 'membership.id'),
		role: value.role,
		status: 'ACTIVE',
		displayName: nullableText(value.displayName),
		title: nullableText(value.title),
		avatarObjectKey: nullableText(value.avatarObjectKey),
		contacts: contacts(value.contacts || [], true),
	})
}

export function normalizeOrganizationProfile(value) {
	if (!value || !Array.isArray(value.memberships)) throw new TypeError('Invalid profile response')
	return Object.freeze({
		organization: normalizeOrganization(value.organization, true),
		memberships: value.memberships.map(normalizeMembership),
	})
}

export function normalizeDiscoveryResult(value) {
	if (!value) throw new TypeError('Invalid discovery response')
	const organization = normalizeOrganization(value.organization, false)
	const professional = organization.kind === 'PERSONAL'
		? Object.freeze({
			displayName: nullableText(value.professional?.displayName),
			title: nullableText(value.professional?.title),
			avatarObjectKey: nullableText(value.professional?.avatarObjectKey),
			contacts: contacts(value.professional?.contacts || [], false),
		})
		: null
	return Object.freeze({ organization, professional })
}

export class OrganizationProfileApi {
	constructor(http = apiClient) {
		this.http = http
	}

	async getProfile() {
		return normalizeOrganizationProfile((await this.http.get('/api/organization-profile')).data)
	}

	async updateOrganization(patch) {
		return normalizeOrganizationProfile((await this.http.patch('/api/organization-profile', patch)).data)
	}

	async updateVisibilityPolicies(patch) {
		const response = await this.http.patch('/api/organization-profile/policies', patch)
		if (!['ASSIGNED_ONLY', 'ALL_MEMBERS'].includes(response.data?.requestVisibility)
			|| !['ASSIGNED_ONLY', 'ALL_MEMBERS'].includes(response.data?.clientVisibility)
			|| typeof response.data?.requireClientOnRequestCreation !== 'boolean') {
			throw new TypeError('Invalid visibility policy response')
		}
		return Object.freeze({
			requestVisibility: response.data.requestVisibility,
			clientVisibility: response.data.clientVisibility,
			requireClientOnRequestCreation: response.data.requireClientOnRequestCreation,
		})
	}

	async updateMembership(membershipId, patch) {
		const id = positiveId(membershipId, 'membership.id')
		return normalizeOrganizationProfile((await this.http.patch(`/api/organization-memberships/${id}/profile`, patch)).data)
	}

	async discoverByReldoId(reldoId) {
		return normalizeDiscoveryResult((await this.http.get('/api/organization-discovery', { params: { reldoId } })).data)
	}

	async discoverByEmail(email) {
		const result = normalizeDiscoveryResult((await this.http.get('/api/organization-discovery', { params: { email } })).data)
		if (result.organization.kind !== 'PERSONAL') throw new TypeError('Email discovery must return PERSONAL Organization')
		return result
	}
}

export default new OrganizationProfileApi()
